/**
 * Meili Additional Data Encoder
 *
 * This helper encodes additional data for Meili to enhance personalization
 * and boost conversion as per Meili documentation.
 *
 * Based on: https://meili.atlassian.net/servicedesk/customer/portal/1/article/1680375827
 */

export interface MeiliAdditionalData {
  // Customer identification
  customerPartnerCode?: string;
  partnerCustomerID?: string;

  // Trip information
  numberOfPassengers?: number;

  // Flight information
  flightNumber?: string;
  passengerNameRecord?: string;
  superPassengerNameRecord?: string;

  // Passenger details
  infant?: number;
  child?: number;
  teenager?: number;

  // Loyalty and fare information
  supplierLoyaltyAccounts?: Array<{ customerAccount: string }>;
  fareTypeAndFlex?: string;
  partnerLoyaltyAccount?: string;
  partnerLoyaltyAccountTier?: string;

  // Location information
  departureAirport?: string;
  arrivalAirport?: string;

  // Pricing information
  airlineFareAmount?: number;
  airlineFareCurrency?: string;

  // Customer information
  firstName?: string;
  lastName?: string;
  email?: string;
  phoneNumbers?: string[];
  companyName?: string;
  corporateCode?: string;

  // Address information
  addressLine1?: string;
  postCode?: string;
  city?: string;
  state?: string;

  // Business information
  agentId?: string;
  iataNumber?: string;

  // Additional preferences
  ancillaryActivity?: string;

  // Custom additional data
  additionalData?: Record<string, any>;
}

/**
 * Converts a JSON object containing Meili additional data into a base64 encoded string.
 *
 * @param data - JSON object which holds information about the user's booking, search etc.
 * @returns A base64 encoded version of the data object.
 */
export function encodeMeiliAdditionalData(data: MeiliAdditionalData[]): string {
  // Convert the JSON data to a string
  const dataString = JSON.stringify(data);

  // Use Unicode-safe base64 encoding
  // First encode to UTF-8, then to base64
  const utf8Bytes = new TextEncoder().encode(dataString);
  const binaryString = Array.from(utf8Bytes, (byte) => String.fromCharCode(byte)).join('');
  return btoa(binaryString);
}

/**
 * Creates Meili additional data object with wego_click_id for car rentals.
 *
 * @param wegoClickId - The click ID to be passed as customerPartnerCode
 */
export function createMeiliDataWithWegoClickId(wegoClickId: string): MeiliAdditionalData[] {
  return [
    {
      customerPartnerCode: wegoClickId,
    },
  ];
}
